import { perceptualGain, type AudioBus, type Lane } from './audioBus';
import { MUSIC_VOLUME_DEFAULT } from './radio';

/**
 * The ambiences (audit step 26b): recorded loops, CC0 from Freesound (see CREDITS.md), made
 * seamless by `tools/assets/ambience_loop.py`. An arena's theme names one (`forest-day`); the
 * temple's is the table's.
 */
export const AMBIENCES: Readonly<Record<string, string>> = {
  'forest-day': '/audio/ambience/forest-day.ogg',
  'forest-night': '/audio/ambience/forest-night.ogg',
  temple: '/audio/ambience/temple.ogg',
};

/**
 * The table's loop between duels (x-aaa-gap#19): the wind in the valley round the temple and its
 * birds far off. The table's sun never sets, so it has no night one.
 */
export const TABLE_AMBIENCE: string | null = AMBIENCES.temple ?? null;

/**
 * Under the fight's own sounds, as the wind and the birds are under a voice: a third of the 0.35
 * it first had, soft enough to be noticed rather than listened to (the owner, 2026-09-26). The
 * ambience follows the Music slider since, not the Sound slider (the owner, the same day); the
 * level is carried over from the Sound slider's default (0.8) to the Music slider's (0.6), so that
 * with both where they start it is as loud as it was.
 */
export const AMBIENCE_GAIN =
  (0.35 / 3) * (perceptualGain(0.8) / perceptualGain(MUSIC_VOLUME_DEFAULT));

/**
 * The table's wind, which the owner hears as waves, another half down on top of that (the owner,
 * 2026-09-26): a low shelf of −6 dB under 2.5 kHz on the temple loop. Measured on the recording,
 * its steady bed and its gusts lie mostly below 2 kHz and its birds between 4 and 8 kHz, so the
 * shelf takes the wind to 0.51 of its amplitude and the birds down by less than 0.3 dB.
 */
export const WAVES_SHELF = { frequency: 2500, gain: -6 } as const;
/** A loop's own shaping where it has one: only the table's, whose wind is its waves. */
const SHELVES: Readonly<Record<string, typeof WAVES_SHELF>> = TABLE_AMBIENCE
  ? { [TABLE_AMBIENCE]: WAVES_SHELF }
  : {};
/** Seconds an ambience takes to come in, or to go when the duel ends. */
const FADE_SECONDS = 1.5;

/**
 * The loop an arena plays for its theme's ambience, by the light of its square: a `-day` ambience
 * gives way to its `-night` at night where there is one. Null for a theme with none, or one no
 * loop is known for.
 */
export function ambienceFile(ambience: string | undefined, night: boolean): string | null {
  if (!ambience) return null;
  const byNight = night && ambience.endsWith('-day') ? `${ambience.slice(0, -4)}-night` : ambience;
  return AMBIENCES[byNight] ?? AMBIENCES[ambience] ?? null;
}

type Fetch = (url: string) => Promise<ArrayBuffer>;
const fetchBytes: Fetch = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url}: ${response.status}`);
  return response.arrayBuffer();
};

/**
 * Plays one ambience at a time, looped, on a lane of its own outside the voice cap (as the radio
 * has): a footstep never steals the forest, and the forest never steals a scream. It follows the
 * Music slider (`setVolume`), beside the master volume and mute that the Sound slider sets. A new
 * one fades in over the last, and `play(null)` fades it out. Loops are fetched and decoded once
 * each. Before the first gesture there is no context: it stays silent, and the next `play` tries
 * again.
 */
export class AmbiencePlayer {
  private lane: Lane | null = null;
  private current: {
    file: string;
    source: AudioBufferSourceNode;
    gain: GainNode;
    ctx: BaseAudioContext;
  } | null = null;
  private wanted: string | null = null;
  private readonly buffers = new Map<string, Promise<AudioBuffer>>();
  /** Where the Music slider stands, 0 … 1. */
  private music = MUSIC_VOLUME_DEFAULT;

  constructor(
    private readonly bus: AudioBus,
    private readonly fetchFile: Fetch = fetchBytes,
  ) {}

  /** The loop playing or coming in, for tests. */
  get playing(): string | null {
    return this.current?.file ?? null;
  }

  /** Follows the Music slider (0 … 1), on the same curve as the other sliders, eased. */
  setVolume(music: number): void {
    this.music = Math.min(1, Math.max(0, music));
    const lane = this.lane?.alive ? this.lane : null;
    lane?.input.gain.setTargetAtTime(this.level(), lane.context.currentTime, 0.03);
  }

  /** The lane's gain for the Music slider as it stands. */
  private level(): number {
    return AMBIENCE_GAIN * perceptualGain(this.music);
  }

  play(file: string | null): void {
    this.wanted = file;
    if (file === (this.current?.file ?? null)) return;
    this.fadeOut();
    if (!file) return;
    const lane = this.laneNow();
    if (!lane) return;
    void this.buffer(lane.context, file).then(
      (buffer) => {
        // Another ambience, or none, was asked for while this one loaded.
        if (this.wanted !== file || this.current || !lane.alive) return;
        const ctx = lane.context;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(1, ctx.currentTime + FADE_SECONDS);
        gain.connect(lane.input);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.loop = true;
        const shelf = SHELVES[file];
        if (shelf) {
          const filter = ctx.createBiquadFilter();
          filter.type = 'lowshelf';
          filter.frequency.value = shelf.frequency;
          filter.gain.value = shelf.gain;
          source.connect(filter);
          filter.connect(gain);
        } else source.connect(gain);
        source.start();
        this.current = { file, source, gain, ctx };
      },
      (err: unknown) => console.warn('ambience failed to load', err),
    );
  }

  dispose(): void {
    this.wanted = null;
    this.fadeOut();
    this.lane?.dispose();
    this.lane = null;
  }

  private fadeOut(): void {
    const playing = this.current;
    this.current = null;
    if (!playing) return;
    const now = playing.ctx.currentTime;
    playing.gain.gain.cancelScheduledValues(now);
    playing.gain.gain.setValueAtTime(playing.gain.gain.value, now);
    playing.gain.gain.linearRampToValueAtTime(0, now + FADE_SECONDS);
    playing.source.stop(now + FADE_SECONDS + 0.05);
    playing.source.onended = () => playing.gain.disconnect();
  }

  private laneNow(): Lane | null {
    if (this.lane?.alive) return this.lane;
    this.lane = this.bus.lane({ gain: this.level(), besideMaster: true });
    return this.lane;
  }

  private buffer(ctx: BaseAudioContext, file: string): Promise<AudioBuffer> {
    let buffer = this.buffers.get(file);
    if (!buffer) {
      buffer = this.fetchFile(file).then((bytes) => ctx.decodeAudioData(bytes));
      // A failure is not kept: the next duel tries again.
      buffer.catch(() => this.buffers.delete(file));
      this.buffers.set(file, buffer);
    }
    return buffer;
  }
}
